import React from 'react';

import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
} from '@floating-ui/react';

import { cn } from 'utils/cn';
import { usePopoverContext } from './Provider';

export interface PopoverContentProps extends React.HTMLProps<HTMLDivElement> {
  closeOnFocusOut?: boolean;
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
}

export const PopoverContent = React.forwardRef<
  HTMLDivElement,
  PopoverContentProps
>(function PopoverContent(
  { style, className, closeOnFocusOut = true, initialFocus, ...props },
  propRef,
) {
  const { context: floatingContext, ...context } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        closeOnFocusOut={closeOnFocusOut}
        initialFocus={initialFocus}
      >
        <div
          ref={ref}
          aria-describedby={context.descriptionId}
          aria-labelledby={context.labelId}
          className={cn(
            'max-w-[calc(100dvw-48px)] max-h-[calc(100dvh-48px)]',
            'bg-background-inverse border border-solid rounded-medium',
            '[&::-webkit-scrollbar]:w-xs2 [&::-webkit-scrollbar]:rounded-full',
            '[&::-webkit-scrollbar-thumb]:bg-[var(--tw-grey-70)] [&::-webkit-scrollbar-thumb]:rounded-full',
            '[&::-webkit-scrollbar-track]:shadow-[inset_0_0_6px_rgb(0_0_0/30%)]',
            className,
          )}
          style={{ ...context.floatingStyles, ...style }}
          {...context.getFloatingProps(props)}
        >
          {props.children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
});
